import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/ui/Toast';
import { Button, Input, Textarea, Select, Card, Badge } from '../components/ui';

// Opzioni per select
const STATO_OPTIONS = [
  { value: 100000000, label: 'Pianificato' },
  { value: 100000001, label: 'In scadenza' },
  { value: 100000002, label: 'Scaduto' },
  { value: 100000003, label: 'Eseguito' },
  { value: 100000004, label: 'Annullato' }
];

const PRIORITA_OPTIONS = [
  { value: 100000000, label: 'Bassa' },
  { value: 100000001, label: 'Media' },
  { value: 100000002, label: 'Alta' },
  { value: 100000003, label: 'Urgente' }
];

function NuovaScadenzaPage() {
  const { user, apiRequest } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Dati per select
  const [fornitori, setFornitori] = useState([]);
  const [rsaList, setRsaList] = useState([]);
  const [tipologieIntervento, setTipologieIntervento] = useState([]);
  const [tipologieFornitore, setTipologieFornitore] = useState([]); // Tipologie filtrate per fornitore
  const [contratti, setContratti] = useState([]);

  // Tipologie selezionate (multi-selezione)
  const [tipologieSelezionate, setTipologieSelezionate] = useState([]);
  const [loadingTipologie, setLoadingTipologie] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    nome: '',
    rsaId: searchParams.get('rsaId') || '',
    fornitoreId: searchParams.get('fornitoreId') || '',
    contrattoId: searchParams.get('contrattoId') || '',
    dataScadenza: '',
    dataNotifica: '',
    stato: 100000000, // Pianificato di default
    priorita: 100000001, // Media di default
    note: ''
  });

  // Check permissions
  const isAdmin = user?.ruolo === 100000000;
  const isRspp = user?.ruolo === 100000001;
  const canCreate = isAdmin || isRspp;

  useEffect(() => {
    if (!canCreate) {
      showToast('Non hai i permessi per creare scadenze', 'error');
      navigate('/scadenze');
      return;
    }

    const fetchData = async () => {
      try {
        const [fornitoriRes, rsaRes, tipologieRes, contrattiRes] = await Promise.all([
          apiRequest('/fornitori'),
          apiRequest('/rsa'),
          apiRequest('/tipologie-intervento'),
          apiRequest('/contratti')
        ]);

        setFornitori(fornitoriRes.value || []);
        setRsaList(rsaRes.value || []);
        setTipologieIntervento(tipologieRes.value || []);
        setContratti(contrattiRes.value || []);
      } catch (error) {
        console.error('Error fetching data:', error);
        showToast('Errore nel caricamento dati', 'error');
      }
    };

    fetchData();
  }, [apiRequest, canCreate, navigate, showToast]);

  // Carica tipologie quando cambia il fornitore
  useEffect(() => {
    const loadTipologieFornitore = async () => {
      if (!formData.fornitoreId) {
        setTipologieFornitore([]);
        setTipologieSelezionate([]);
        return;
      }

      setLoadingTipologie(true);
      try {
        // Prova a caricare tipologie associate al fornitore via N:N
        const res = await apiRequest(`/fornitori/${formData.fornitoreId}/tipologie-intervento`);
        if (res.value && res.value.length > 0) {
          setTipologieFornitore(res.value);
        } else {
          // Fallback: mostra tutte le tipologie se il fornitore non ha associazioni
          setTipologieFornitore(tipologieIntervento);
        }
      } catch (error) {
        console.log('Tipologie fornitore non disponibili, uso tutte:', error.message);
        // Fallback: mostra tutte le tipologie
        setTipologieFornitore(tipologieIntervento);
      } finally {
        setLoadingTipologie(false);
      }
      // Reset selezione quando cambia fornitore
      setTipologieSelezionate([]);
    };

    loadTipologieFornitore();
  }, [formData.fornitoreId, apiRequest, tipologieIntervento]);

  // Calcola data scadenza automatica quando si seleziona la prima tipologia
  const calcolaDataScadenza = (tipologia) => {
    if (!tipologia?.frequenzaValore || !tipologia?.frequenzaUnita) return null;

    const oggi = new Date();
    let dataScadenza = new Date(oggi);

    switch (tipologia.frequenzaUnita) {
      case 100000000: // Giorni
        dataScadenza.setDate(oggi.getDate() + tipologia.frequenzaValore);
        break;
      case 100000001: // Settimane
        dataScadenza.setDate(oggi.getDate() + (tipologia.frequenzaValore * 7));
        break;
      case 100000002: // Mesi
        dataScadenza.setMonth(oggi.getMonth() + tipologia.frequenzaValore);
        break;
      case 100000003: // Anni
        dataScadenza.setFullYear(oggi.getFullYear() + tipologia.frequenzaValore);
        break;
      default:
        return null;
    }

    return dataScadenza.toISOString().split('T')[0];
  };

  // Filtra contratti per RSA/Fornitore selezionati
  const filteredContratti = contratti.filter(c => {
    if (formData.rsaId && c.rsaId && c.rsaId !== formData.rsaId) return false;
    if (formData.fornitoreId && c.fornitore?.id !== formData.fornitoreId) return false;
    return c.stato === 100000000; // Solo contratti attivi
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error on change
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Reset contratto when RSA or Fornitore changes
    if (name === 'rsaId' || name === 'fornitoreId') {
      setFormData(prev => ({
        ...prev,
        contrattoId: ''
      }));
    }
  };

  // Gestione selezione tipologie multiple
  const handleTipologiaToggle = (tipologiaId) => {
    setTipologieSelezionate(prev => {
      const newSelection = prev.includes(tipologiaId)
        ? prev.filter(id => id !== tipologiaId)
        : [...prev, tipologiaId];

      // Auto-genera nome e calcola data scadenza
      if (newSelection.length > 0) {
        const nomiTipologie = newSelection
          .map(id => tipologieFornitore.find(t => t.id === id)?.nome)
          .filter(Boolean);

        // Se è la prima selezione, calcola anche la data scadenza
        const primaTipologia = tipologieFornitore.find(t => t.id === newSelection[0]);
        const updates = {};

        if (!formData.nome) {
          updates.nome = `Scadenza ${nomiTipologie.join(' + ')}`;
        }

        // Auto-calcola data scadenza se non già impostata
        if (!formData.dataScadenza && primaTipologia) {
          const dataSuggerita = calcolaDataScadenza(primaTipologia);
          if (dataSuggerita) {
            updates.dataScadenza = dataSuggerita;
          }
        }

        if (Object.keys(updates).length > 0) {
          setFormData(f => ({ ...f, ...updates }));
        }
      }

      return newSelection;
    });
  };

  // Filtra tipologie per stessa frequenza (come in NuovoInterventoPage)
  const primaSelezionata = tipologieSelezionate.length > 0
    ? tipologieFornitore.find(t => t.id === tipologieSelezionate[0])
    : null;

  const tipologieFiltrate = primaSelezionata
    ? tipologieFornitore.filter(t =>
        t.frequenzaValore === primaSelezionata.frequenzaValore &&
        t.frequenzaUnita === primaSelezionata.frequenzaUnita
      )
    : tipologieFornitore;

  const validate = () => {
    const newErrors = {};

    if (!formData.nome.trim()) {
      newErrors.nome = 'Il nome della scadenza e obbligatorio';
    }
    if (!formData.rsaId) {
      newErrors.rsaId = 'Seleziona una RSA';
    }
    if (!formData.fornitoreId) {
      newErrors.fornitoreId = 'Seleziona un fornitore';
    }
    if (!formData.dataScadenza) {
      newErrors.dataScadenza = 'La data di scadenza e obbligatoria';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      showToast('Correggi gli errori nel form', 'error');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        nome: formData.nome.trim(),
        rsaId: formData.rsaId,
        fornitoreId: formData.fornitoreId,
        // Usa la prima tipologia per il lookup singolo (compatibilita)
        tipologiaInterventoId: tipologieSelezionate.length > 0 ? tipologieSelezionate[0] : null,
        // Invia tutte le tipologie per associazione N:N
        tipologieIds: tipologieSelezionate.length > 0 ? tipologieSelezionate : null,
        contrattoId: formData.contrattoId || null,
        dataScadenza: formData.dataScadenza,
        dataNotifica: formData.dataNotifica || null,
        stato: formData.stato,
        priorita: formData.priorita,
        note: formData.note.trim() || null
      };

      await apiRequest('/scadenze', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      showToast('Scadenza creata con successo', 'success');
      navigate('/scadenze');
    } catch (error) {
      console.error('Error creating scadenza:', error);
      showToast(error.message || 'Errore nella creazione della scadenza', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Calcola data notifica suggerita (7 giorni prima della scadenza)
  const suggestNotificationDate = () => {
    if (formData.dataScadenza) {
      const scadenza = new Date(formData.dataScadenza);
      scadenza.setDate(scadenza.getDate() - 7);
      setFormData(prev => ({
        ...prev,
        dataNotifica: scadenza.toISOString().split('T')[0]
      }));
    }
  };

  if (!canCreate) {
    return null;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Nuova Scadenza</h1>
          <p className="text-gray-600">Programma una nuova scadenza di manutenzione</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/scadenze')}>
          Annulla
        </Button>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Informazioni Base */}
        <Card className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Informazioni Base</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Nome Scadenza *"
              name="nome"
              value={formData.nome}
              onChange={handleChange}
              error={errors.nome}
              placeholder="es. Manutenzione Ascensore Q1"
            />

{/* La selezione tipologie è in una sezione dedicata sotto */}

            <Select
              label="RSA *"
              name="rsaId"
              value={formData.rsaId}
              onChange={handleChange}
              error={errors.rsaId}
              options={[
                { value: '', label: 'Seleziona RSA...' },
                ...rsaList.map(r => ({ value: r.id, label: r.nome }))
              ]}
            />

            <Select
              label="Fornitore *"
              name="fornitoreId"
              value={formData.fornitoreId}
              onChange={handleChange}
              error={errors.fornitoreId}
              options={[
                { value: '', label: 'Seleziona fornitore...' },
                ...fornitori.map(f => ({ value: f.id, label: f.nome }))
              ]}
            />

            <Select
              label="Contratto"
              name="contrattoId"
              value={formData.contrattoId}
              onChange={handleChange}
              helper="Opzionale - collega a un contratto esistente"
              options={[
                { value: '', label: 'Nessun contratto' },
                ...filteredContratti.map(c => ({ value: c.id, label: `${c.nome} (${c.fornitore?.nome})` }))
              ]}
            />
          </div>
        </Card>

        {/* Tipologie Intervento - Selezione Multipla */}
        <Card className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Tipologie Intervento</h2>
          <p className="text-sm text-gray-600 mb-4">
            Seleziona una o più tipologie di intervento per questa scadenza.
            {primaSelezionata && (
              <span className="block mt-1 text-primary-600">
                Filtro attivo: solo tipologie con frequenza {primaSelezionata.frequenzaValore}{' '}
                {primaSelezionata.frequenzaUnita === 100000000 ? 'giorni' :
                 primaSelezionata.frequenzaUnita === 100000001 ? 'settimane' :
                 primaSelezionata.frequenzaUnita === 100000002 ? 'mesi' : 'anni'}
              </span>
            )}
          </p>

          {tipologieSelezionate.length > 0 && (
            <div className="mb-4 flex flex-wrap gap-2">
              {tipologieSelezionate.map(tipId => {
                const tip = tipologieFornitore.find(t => t.id === tipId);
                return tip ? (
                  <Badge
                    key={tipId}
                    variant="primary"
                    className="cursor-pointer"
                    onClick={() => handleTipologiaToggle(tipId)}
                  >
                    {tip.nome} ✕
                  </Badge>
                ) : null;
              })}
            </div>
          )}

          {!formData.fornitoreId ? (
            <div className="border rounded-lg p-6 text-center bg-gray-50">
              <p className="text-gray-500">
                Seleziona prima un fornitore per vedere le tipologie di intervento disponibili
              </p>
            </div>
          ) : loadingTipologie ? (
            <div className="border rounded-lg p-6 text-center">
              <p className="text-gray-500">Caricamento tipologie...</p>
            </div>
          ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-64 overflow-y-auto border rounded-lg p-3">
            {tipologieFiltrate.length === 0 ? (
              <p className="text-gray-500 col-span-full text-center py-4">
                Nessuna tipologia disponibile per questo fornitore
              </p>
            ) : (
              tipologieFiltrate.map(tipologia => {
                const isSelected = tipologieSelezionate.includes(tipologia.id);
                const isDisabled = primaSelezionata &&
                  (tipologia.frequenzaValore !== primaSelezionata.frequenzaValore ||
                   tipologia.frequenzaUnita !== primaSelezionata.frequenzaUnita);

                return (
                  <label
                    key={tipologia.id}
                    className={`
                      flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer
                      ${isSelected
                        ? 'bg-primary-50 border-primary-500'
                        : isDisabled
                          ? 'bg-gray-50 border-gray-200 opacity-50 cursor-not-allowed'
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                      }
                    `}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => !isDisabled && handleTipologiaToggle(tipologia.id)}
                      disabled={isDisabled}
                      className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                    />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${isSelected ? 'text-primary-900' : 'text-gray-900'}`}>
                        {tipologia.nome}
                      </p>
                      {tipologia.frequenzaValore && (
                        <p className="text-xs text-gray-500">
                          Frequenza: {tipologia.frequenzaValore}{' '}
                          {tipologia.frequenzaUnita === 100000000 ? 'giorni' :
                           tipologia.frequenzaUnita === 100000001 ? 'settimane' :
                           tipologia.frequenzaUnita === 100000002 ? 'mesi' : 'anni'}
                        </p>
                      )}
                    </div>
                  </label>
                );
              })
            )}
          </div>
          )}
        </Card>

        {/* Date */}
        <Card className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Date e Scadenza</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              type="date"
              label="Data Scadenza *"
              name="dataScadenza"
              value={formData.dataScadenza}
              onChange={handleChange}
              error={errors.dataScadenza}
            />

            <div className="flex gap-2">
              <Input
                type="date"
                label="Data Notifica"
                name="dataNotifica"
                value={formData.dataNotifica}
                onChange={handleChange}
                helper="Quando inviare il promemoria"
                className="flex-1"
              />
              <div className="flex items-end">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={suggestNotificationDate}
                  disabled={!formData.dataScadenza}
                >
                  -7gg
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {/* Stato e Priorita */}
        <Card className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Stato e Priorita</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Stato"
              name="stato"
              value={formData.stato}
              onChange={handleChange}
              options={STATO_OPTIONS}
            />

            <Select
              label="Priorita"
              name="priorita"
              value={formData.priorita}
              onChange={handleChange}
              options={PRIORITA_OPTIONS}
            />
          </div>
        </Card>

        {/* Note */}
        <Card className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Note</h2>

          <Textarea
            name="note"
            value={formData.note}
            onChange={handleChange}
            rows={4}
            placeholder="Eventuali note o istruzioni per questa scadenza..."
          />
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/scadenze')}
          >
            Annulla
          </Button>
          <Button
            type="submit"
            loading={loading}
          >
            Crea Scadenza
          </Button>
        </div>
      </form>
    </div>
  );
}

export default NuovaScadenzaPage;
